import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";

// Kyte-Doolittle hydrophobicity scale (1982)
// Reference: Kyte J, Doolittle RF. J Mol Biol. 157(1):105-32
// Positive = hydrophobic, Negative = hydrophilic
const KD_SCALE = {
  ILE:  4.5, VAL:  4.2, LEU:  3.8, PHE:  2.8, CYS:  2.5,
  MET:  1.9, ALA:  1.8, GLY: -0.4, THR: -0.7, SER: -0.8,
  TRP: -0.9, TYR: -1.3, PRO: -1.6, HIS: -3.2, GLU: -3.5,
  GLN: -3.5, ASP: -3.5, ASN: -3.5, LYS: -3.9, ARG: -4.5,
};

// Order on X axis: from most hydrophobic to most hydrophilic
const AA_ORDER = Object.entries(KD_SCALE)
  .sort((a, b) => b[1] - a[1])
  .map(([aa]) => aa);

const AAHydrophobicityChart = ({ contactData }) => {
  const [plotData, setPlotData] = useState([]);
  const [layout, setLayout] = useState({});

  useEffect(() => {

    if (!Array.isArray(contactData) || contactData.length === 0) {
      setPlotData([]);
      return;
    }

    const counts = {};
    AA_ORDER.forEach((aa) => { counts[aa] = 0; });

    contactData.forEach(({ details }) => {
      if (!details) return;
      const parts = details.split('|');
      for (let i = 0; i < parts.length; i++) {
        const tokens = parts[i].trim().split('/');
        if (tokens.length >= 4) {
          const aa = tokens[3].trim().toUpperCase();
          if (aa in counts) counts[aa] += 1;
        }
      }
    });

    const xLabels = AA_ORDER;
    const yCounts = AA_ORDER.map((aa) => counts[aa]);
    const kdValues = AA_ORDER.map((aa) => KD_SCALE[aa]);

    setPlotData([
      {
        x: xLabels,
        y: yCounts,
        customdata: kdValues,
        type: "bar",
        hovertemplate:
          "Residue: %{x}<br>Count: %{y}<br>Kyte-Doolittle: %{customdata:.1f}<extra></extra>",
        marker: {
          color: kdValues,
          colorscale: [
            [0,    "#1f78b4"], // strong hydrophilic — blue
            [0.35, "#a6cee3"], // mild hydrophilic
            [0.5,  "#f7f7f7"], // neutral — white
            [0.65, "#fdae61"], // mild hydrophobic
            [1,    "#d73027"], // strong hydrophobic — red
          ],
          cmin: -4.5,
          cmax:  4.5,
          colorbar: {
             title: {
            text: "Kyte-Doolittle",
            side: "right",
          },
          thickness: 12,
            tickvals: [-4.5, -2, 0, 2, 4.5],
            ticktext: ["−4.5<br>(hydrophilic)", "−2", "0", "2", "+4.5<br>(hydrophobic)"],
          },
          line: { color: "#333", width: 0.5 },
        },
      },
    ]);

    setLayout({
      autosize: true,
       font: { size: 9 },
      title: { text: "AA Frequency Weighted by Hydrophobicity" },
      xaxis: {
        type: "category",
        categoryorder: "array",
        categoryarray: AA_ORDER,
        title: { text: "Amino acid (sorted by Kyte-Doolittle)" },
        tickangle: -45,
      },
      yaxis: {
        title: { text: "# of occurrences" },
      },
      margin: { t: 50, b: 80, l: 60, r: 80 },
    });
  }, [contactData]);

  return (
    <div className="aspect-square card">
      <Plot
        data={plotData}
        layout={layout}
        useResizeHandler
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
};

export default AAHydrophobicityChart;
